<div class="chemical-report-container">
  <header class="report-header">
    <h1>化學物質管理分析報表</h1>
    <div class="timeframe-selector">
      <label>時間範圍:</label>
      <div class="timeframe-buttons">
        <button [class.active]="selectedTimeframe === 'month'" (click)="selectedTimeframe = 'month'">月</button>
        <button [class.active]="selectedTimeframe === 'quarter'" (click)="selectedTimeframe = 'quarter'">季</button>
        <button [class.active]="selectedTimeframe === 'year'" (click)="selectedTimeframe = 'year'">年</button>
      </div>
    </div>
  </header>

  <!-- 載入中提示 -->
  <div class="loading-container" *ngIf="loading">
    <div class="loading-spinner"></div>
    <span>載入中，請稍候...</span>
  </div>

  <!-- 錯誤提示 -->
  <div class="error-message" *ngIf="error">
    {{ error }}
  </div>

  <!-- 主要內容 -->
  <div class="report-content" *ngIf="!loading && !error">
    <!-- 分頁標籤 -->
    <div class="tabs">
      <button [class.active]="activeTab === 'overview'" (click)="setActiveTab('overview')">總覽報表</button>
      <button [class.active]="activeTab === 'chemicals'" (click)="setActiveTab('chemicals')">化學物質風險</button>
      <button [class.active]="activeTab === 'departments'" (click)="setActiveTab('departments')">部門風險</button>
      <button [class.active]="activeTab === 'todos'" (click)="setActiveTab('todos')">待辦事項</button>
    </div>

    <!-- 總覽分頁 -->
    <div class="tab-content" *ngIf="activeTab === 'overview'">
      <div class="summary-cards">
        <div class="summary-card">
          <div class="card-title">化學物質總數</div>
          <div class="card-value">{{ chemicals.length }}</div>
          <div class="card-footer">{{ getHighRiskChemicals().length }} 項高風險</div>
        </div>
        <div class="summary-card">
          <div class="card-title">部門總數</div>
          <div class="card-value">{{ departments.length }}</div>
          <div class="card-footer">{{ getHighRiskDepartments().length }} 個高風險部門</div>
        </div>
        <div class="summary-card">
          <div class="card-title">待辦事項</div>
          <div class="card-value">{{ todoItems.length }}</div>
          <div class="card-footer">{{ getUrgentTodos().length }} 項緊急事項</div>
        </div>
        <div class="summary-card">
          <div class="card-title">過期SDS</div>
          <div class="card-value">{{ getExpiredSDS().length }}</div>
          <div class="card-footer">{{ getExpiringSoonSDS().length }} 項即將到期</div>
        </div>
      </div>

      <div class="charts-container">
        <div class="chart-row">
          <div class="chart-wrapper half-width">
            <div class="chart-container" echarts [options]="highRiskChartOption"></div>
          </div>
          <div class="chart-wrapper half-width">
            <div class="chart-container" echarts [options]="departmentRiskChartOption"></div>
          </div>
        </div>
        <div class="chart-row">
          <div class="chart-wrapper half-width">
            <div class="chart-container" echarts [options]="chemicalCountChartOption"></div>
          </div>
          <div class="chart-wrapper half-width">
            <div class="chart-container" echarts [options]="expiryStatusChartOption"></div>
          </div>
        </div>
      </div>

      <div class="overview-lists">
        <div class="list-section">
          <h2>需立即注意的高風險物質</h2>
          <table class="data-table">
            <thead>
              <tr>
                <th>名稱</th>
                <th>CAS號</th>
                <th>風險等級</th>
                <th>使用部門</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let chem of getHighRiskChemicals().slice(0, 5)">
                <td>{{ chem.chineseName }}</td>
                <td>{{ chem.casNumber }}</td>
                <td>
                  <span class="risk-level" [class]="'risk-' + chem.riskLevel">{{ chem.riskLevel }}</span>
                </td>
                <td>{{ chem.departmentName }}</td>
              </tr>
              <tr *ngIf="getHighRiskChemicals().length === 0">
                <td colspan="4" class="no-data">沒有高風險化學物質</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="list-section">
          <h2>優先處理的待辦事項</h2>
          <table class="data-table">
            <thead>
              <tr>
                <th>項目</th>
                <th>截止日期</th>
                <th>優先級</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let todo of getUrgentTodos().slice(0, 5)">
                <td>{{ todo.title }}</td>
                <td>{{ formatDate(todo.dueDate) }}</td>
                <td>
                  <span class="priority-badge" [class]="'priority-' + todo.priority">
                    {{ todo.priority === 'high' ? '高' : todo.priority === 'medium' ? '中' : '低' }}
                  </span>
                </td>
              </tr>
              <tr *ngIf="getUrgentTodos().length === 0">
                <td colspan="3" class="no-data">沒有緊急待辦事項</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 化學物質分頁 -->
    <div class="tab-content" *ngIf="activeTab === 'chemicals'">
      <h2>化學物質風險分析</h2>

      <div class="section-container">
        <h3>高風險化學物質（風險等級 IV-V）</h3>
        <p class="section-description">
          以下化學物質具有較高的風險等級，建議優先考慮替代或加強控制措施。
        </p>

        <table class="data-table">
          <thead>
            <tr>
              <th>中文名稱</th>
              <th>英文名稱</th>
              <th>CAS號</th>
              <th>風險等級</th>
              <th>風險分數</th>
              <th>使用部門</th>
              <th>關鍵危害特性</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let chemical of getHighRiskChemicals()">
              <td>{{ chemical.chineseName }}</td>
              <td>{{ chemical.englishName }}</td>
              <td>{{ chemical.casNumber }}</td>
              <td>
                <span class="risk-level" [class]="'risk-' + chemical.riskLevel">{{ chemical.riskLevel }}</span>
              </td>
              <td>{{ chemical.riskScore }}</td>
              <td>{{ chemical.departmentName }}</td>
              <td>
                <span class="hazard-tag" *ngFor="let hazard of chemical.hazardClassifications.slice(0, 2)">{{ hazard }}</span>
                <span *ngIf="chemical.hazardClassifications.length > 2">+{{ chemical.hazardClassifications.length - 2 }}</span>
              </td>
            </tr>
            <tr *ngIf="getHighRiskChemicals().length === 0">
              <td colspan="7" class="no-data">沒有高風險化學物質</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="section-container">
        <h3>SDS審核狀態</h3>
        <p class="section-description">
          以下化學物質的SDS已過期或即將到期，需盡快更新。
        </p>

        <h4>已過期SDS</h4>
        <table class="data-table">
          <thead>
            <tr>
              <th>化學品名稱</th>
              <th>CAS號</th>
              <th>部門</th>
              <th>上次更新日</th>
              <th>審核到期日</th>
              <th>過期時間</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let chemical of getExpiredSDS()">
              <td>{{ chemical.chineseName }}</td>
              <td>{{ chemical.casNumber }}</td>
              <td>{{ chemical.departmentName }}</td>
              <td>{{ formatDate(chemical.lastUpdated) }}</td>
              <td>{{ formatDate(chemical.nextReviewDate) }}</td>
              <td class="expired">已過期</td>
            </tr>
            <tr *ngIf="getExpiredSDS().length === 0">
              <td colspan="6" class="no-data">沒有過期的SDS</td>
            </tr>
          </tbody>
        </table>

        <h4>即將到期SDS (三個月內)</h4>
        <table class="data-table">
          <thead>
            <tr>
              <th>化學品名稱</th>
              <th>CAS號</th>
              <th>部門</th>
              <th>上次更新日</th>
              <th>審核到期日</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let chemical of getExpiringSoonSDS()">
              <td>{{ chemical.chineseName }}</td>
              <td>{{ chemical.casNumber }}</td>
              <td>{{ chemical.departmentName }}</td>
              <td>{{ formatDate(chemical.lastUpdated) }}</td>
              <td class="expiring-soon">{{ formatDate(chemical.nextReviewDate) }}</td>
            </tr>
            <tr *ngIf="getExpiringSoonSDS().length === 0">
              <td colspan="5" class="no-data">沒有即將到期的SDS</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 部門分頁 -->
    <div class="tab-content" *ngIf="activeTab === 'departments'">
      <h2>部門風險分析</h2>

      <div class="section-container">
        <h3>部門風險摘要</h3>
        <p class="section-description">
          以下顯示各部門化學品使用情況及整體風險評估。
        </p>

        <table class="data-table">
          <thead>
            <tr>
              <th>部門名稱</th>
              <th>位置</th>
              <th>化學品數量</th>
              <th>高風險化學品數量</th>
              <th>平均風險分數</th>
              <th>風險等級</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let dept of departments" [class.high-risk]="dept.riskScore >= 60">
              <td>{{ dept.name }}</td>
              <td>{{ dept.location }}</td>
              <td>{{ dept.chemicalCount }}</td>
              <td>{{ dept.highRiskCount }}</td>
              <td>{{ dept.riskScore }}</td>
              <td>
                <span class="risk-level"
                      [class]="dept.riskScore >= 80 ? 'risk-V' :
                              dept.riskScore >= 60 ? 'risk-IV' :
                              dept.riskScore >= 40 ? 'risk-III' :
                              dept.riskScore >= 20 ? 'risk-II' : 'risk-I'">
                  {{ dept.riskScore >= 80 ? 'V' :
                     dept.riskScore >= 60 ? 'IV' :
                     dept.riskScore >= 40 ? 'III' :
                     dept.riskScore >= 20 ? 'II' : 'I' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="section-container">
        <h3>高風險部門</h3>
        <p class="section-description">
          以下部門具有高風險化學品或整體風險等級較高，建議優先實施控制措施。
        </p>

        <div class="department-cards">
          <div class="department-card" *ngFor="let dept of getHighRiskDepartments()">
            <div class="dept-header">
              <h4>{{ dept.name }}</h4>
              <span class="location">{{ dept.location }}</span>
            </div>
            <div class="dept-stats">
              <div class="stat">
                <span class="stat-value">{{ dept.chemicalCount }}</span>
                <span class="stat-label">化學品總數</span>
              </div>
              <div class="stat">
                <span class="stat-value high-risk-value">{{ dept.highRiskCount }}</span>
                <span class="stat-label">高風險化學品</span>
              </div>
              <div class="stat">
                <span class="stat-value">{{ dept.riskScore }}</span>
                <span class="stat-label">平均風險分數</span>
              </div>
            </div>
            <div class="dept-chemicals">
              <h5>危險化學品清單</h5>
              <ul>
                <ng-container *ngIf="dept && chemicals">
                  <li *ngFor="let chem of getHighRiskChemicalsForDept(dept.id, 3)">
                    {{ chem.chineseName }}
                    <span class="risk-level mini" [class]="'risk-' + chem.riskLevel">{{ chem.riskLevel }}</span>
                  </li>
                  <li *ngIf="getHighRiskChemicalCountForDept(dept.id) > 3">
                    ... 及其他高風險化學品
                  </li>
                  <li *ngIf="getHighRiskChemicalCountForDept(dept.id) === 0">
                    無高風險化學品
                  </li>
                </ng-container>
              </ul>
            </div>
            <div class="dept-recommendations">
              <h5>改善建議</h5>
              <ul>
                <li *ngIf="dept && dept.highRiskCount > 0">實施嚴格的通風和個人防護措施</li>
                <li *ngIf="dept && dept.highRiskCount > 0">考慮替代低風險化學品</li>
                <li *ngIf="dept && dept.riskScore >= 60">加強員工安全培訓</li>
                <li *ngIf="dept && dept.riskScore >= 80">定期進行風險評估和工作場所監測</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 待辦事項分頁 -->
    <div class="tab-content" *ngIf="activeTab === 'todos'">
      <h2>化學品管理待辦事項</h2>

      <div class="section-container">
        <h3>優先處理項目</h3>
        <p class="section-description">
          以下待辦事項需要優先處理，包括法規申報、SDS更新和安全控制措施。
        </p>

        <div class="todo-cards">
          <div class="todo-card" *ngFor="let todo of todoItems" [class]="'priority-' + todo.priority + '-card'">
            <div class="todo-header">
              <h4>{{ todo.title }}</h4>
              <span class="priority-badge" [class]="'priority-' + todo.priority">
                {{ todo.priority === 'high' ? '高優先' : todo.priority === 'medium' ? '中優先' : '低優先' }}
              </span>
            </div>
            <div class="todo-body">
              <p>{{ todo.description }}</p>
              <div class="todo-meta">
                <div class="due-date">
                  <i class="fas fa-calendar"></i>
                  截止日期: {{ formatDate(todo.dueDate) }}
                </div>
                <div class="todo-type">
                  <i class="fas fa-tag"></i>
                  {{
                    todo.type === 'sds-update' ? 'SDS更新' :
                    todo.type === 'legal-report' ? '法規申報' :
                    todo.type === 'substitution' ? '替代評估' :
                    todo.type === 'control-measure' ? '控制措施' :
                    todo.type === 'sds-review' ? 'SDS審核' :
                    '其他'
                  }}
                </div>
              </div>
            </div>
            <div class="todo-footer">
              <button class="status-button" [disabled]="todo.status === 'completed'">
                {{ todo.status === 'completed' ? '已完成' : '標記為完成' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="section-container">
        <h3>化學品管理改善計劃</h3>
        <div class="improvement-plan">
          <div class="plan-group">
            <h4>短期計劃 (1-3個月)</h4>
            <ul>
              <li>完成所有過期SDS的更新</li>
              <li>完成本季公共危險品申報</li>
              <li>為高風險部門實施額外的工程控制措施</li>
            </ul>
          </div>
          <div class="plan-group">
            <h4>中期計劃 (3-6個月)</h4>
            <ul>
              <li>對高風險化學品進行替代品評估</li>
              <li>更新部門化學品清單及風險評估</li>
              <li>完成化學品儲存區域改善計劃</li>
            </ul>
          </div>
          <div class="plan-group">
            <h4>長期計劃 (6-12個月)</h4>
            <ul>
              <li>建立完整的化學品生命週期管理系統</li>
              <li>推動風險較低替代品的全面應用</li>
              <li>實施先進的暴露監測系統</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
